import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import { Focusable, GlobusIcon, IconButton, SortIcon, TimesIcon } from '@onsolve/onsolve-ui-components';

import { sharedTranslations } from 'translations';
import { KeyCodes } from 'common/constants';
import { isCRC168Enabled } from 'featureFlags';

import LanguageList from './LanguageList';

const LanguageDropdown = ({ isOpen, currentLanguage, toggleDropdown, renderClose, iconProps, afterLanguageChange }) => {
    const menuRef = useRef(null);
    const toggleRef = useRef(null);
    const selectedLanguageRef = useRef(null);
    const arrowNavigationRef = useRef(null);
    const intl = useIntl();

    useEffect(() => {
        if (isOpen || selectedLanguageRef.current !== currentLanguage.lcId) {
            return undefined;
        }

        selectedLanguageRef.current = null;
        if (toggleRef.current) {
            toggleRef.current.focus();
        }
    }, [isOpen, currentLanguage.lcId]);

    const focusLanguage = (direction) => {
        const buttons = menuRef.current && menuRef.current.querySelectorAll('button.onsolve-lang');
        const button = buttons && buttons[direction === 'ArrowUp' ? buttons.length - 1 : 0];

        if (button) {
            button.focus();
            if (button.scrollIntoView) {
                button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
        }
    };

    useEffect(() => {
        if (isOpen && arrowNavigationRef.current) {
            focusLanguage(arrowNavigationRef.current);
            arrowNavigationRef.current = null;
        }
    }, [isOpen]);

    const handleLanguageChange = (event, language) => {
        const { ENTER_KEY, SPACE, TAB } = KeyCodes;
        const isSelectKey = isCRC168Enabled()
            ? event.keyCode === ENTER_KEY || event.keyCode === SPACE || event.key === 'Enter' || event.key === ' '
            : event.keyCode === ENTER_KEY;

        if (event.type === 'click' || (event.type === 'keydown' && isSelectKey)) {
            if (isCRC168Enabled()) {
                selectedLanguageRef.current = language.lcId;
            }
            afterLanguageChange(language);
        } else if (event.type === 'keydown' && event.keyCode !== ENTER_KEY && event.keyCode !== TAB) {
            event.preventDefault();
        }
    };

    const handleToggleKeyDown = (event) => {
        const { ENTER_KEY, SPACE } = KeyCodes;

        event.stopPropagation();
        const isArrowUp = event.key === 'ArrowUp' || event.keyCode === 38;
        const isArrowDown = event.key === 'ArrowDown' || event.keyCode === 40;

        if (isArrowUp || isArrowDown) {
            event.preventDefault();
            const direction = isArrowUp ? 'ArrowUp' : 'ArrowDown';

            if (isOpen) {
                focusLanguage(direction);
            } else {
                arrowNavigationRef.current = direction;
                toggleDropdown(event);
            }

            return;
        }

        if (event.key === 'Enter' || event.key === ' ' || event.keyCode === ENTER_KEY || event.keyCode === SPACE) {
            event.preventDefault();
            toggleDropdown(event);
        }

        handleCloseOnEscape(event);
    };

    const handleClose = (event) => {
        toggleDropdown(event);
        if (toggleRef.current) {
            toggleRef.current.focus();
        }
    };

    const handleCloseOnEscape = (event) => {
        if ((event.key === 'Escape' || event.keyCode === KeyCodes.ESC) && isOpen) {
            event.preventDefault();
            event.stopPropagation();
            handleClose(event);
        }
    };

    const handlePopupKeyDown = (event) => {
        event.stopPropagation();
        handleCloseOnEscape(event);
    };

    return (
        <Dropdown isOpen={isOpen} toggle={toggleDropdown}>
            <Focusable
                render={({ classes }) => (
                    <DropdownToggle
                        innerRef={isCRC168Enabled() ? toggleRef : undefined}
                        className={`d-flex align-items-center text-decoration-none p-1 pl-2 onsolve-language-button ${classes}`}
                        aria-label={
                            isCRC168Enabled()
                                ? intl.formatMessage(sharedTranslations.ng_generic_selectLanguage, {
                                      language: currentLanguage.translatedName,
                                      region: currentLanguage.cultureRegionCode,
                                  })
                                : `${currentLanguage.translatedName} ${currentLanguage.cultureRegionCode}`
                        }
                        aria-haspopup={isCRC168Enabled() ? false : true}
                        onKeyDown={isCRC168Enabled() ? handleToggleKeyDown : toggleDropdown}
                    >
                        <span className="mr-1">{currentLanguage.translatedName}</span>
                        <span className="text-uppercase mx-0">({currentLanguage.cultureRegionCode})</span>
                        <SortIcon className="mx-2" {...iconProps} />
                    </DropdownToggle>
                )}
            />
            <DropdownMenu
                className="onsolve-language-wrapper dropdown-menu"
                {...(isCRC168Enabled() ? { role: 'presentation' } : {})}
                right={currentLanguage.direction.toLowerCase() !== 'rtl'}
                onKeyDown={isCRC168Enabled() ? handlePopupKeyDown : undefined}
            >
                <div ref={menuRef} className="onsolve-card card">
                    <div className={`card-header onsolve-card-header ${currentLanguage.direction.toLowerCase()} `}>
                        <div className="onsolve-lang-title">
                            <GlobusIcon className="onsolve-home-globe" color="primary" size="lg" />
                            <span className="onsolve-lang-heading">
                                <FormattedMessage {...sharedTranslations.ng_generic_languages} />
                            </span>
                        </div>
                        {renderClose ? (
                            <IconButton
                                size="xs"
                                onClick={isCRC168Enabled() ? handleClose : toggleDropdown}
                                tabIndex="0"
                                aria-label={[
                                    intl.formatMessage(sharedTranslations.ng_generic_close),
                                    intl.formatMessage(sharedTranslations.ng_generic_languages),
                                ].join(', ')}
                            >
                                <TimesIcon color="secondary" size="sm" />
                            </IconButton>
                        ) : null}
                    </div>
                    <div className="onsolve-language-dropdown-list">
                        <LanguageList
                            currentLanguage={currentLanguage}
                            onLanguageChange={handleLanguageChange}
                            {...(isCRC168Enabled()
                                ? { listLabel: intl.formatMessage(sharedTranslations.ng_generic_languages) }
                                : {})}
                        />
                    </div>
                </div>
            </DropdownMenu>
        </Dropdown>
    );
};

LanguageDropdown.propTypes = {
    afterLanguageChange: PropTypes.func.isRequired,
    currentLanguage: PropTypes.shape({
        direction: PropTypes.string.isRequired,
        cultureRegionCode: PropTypes.string,
        lcId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        translatedName: PropTypes.string,
    }).isRequired,
    toggleDropdown: PropTypes.func.isRequired,
    iconProps: PropTypes.object,
    isOpen: PropTypes.bool,
    renderClose: PropTypes.bool,
};

export default LanguageDropdown;
